import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function fmtDollars(cents) {
    return ((cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>{label}</div>
            <div className="mt-1 text-sm font-medium" style={{ color: "var(--st-navy)" }}>{value ?? "—"}</div>
        </div>
    );
}

export default function AdminAffiliateDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    const [approveOpen, setApproveOpen] = useState(false);
    const [approveReason, setApproveReason] = useState("");

    const [rateOpen, setRateOpen] = useState(false);
    const [newRatePct, setNewRatePct] = useState(20);
    const [rateReason, setRateReason] = useState("");

    const [deactOpen, setDeactOpen] = useState(false);
    const [deactWord, setDeactWord] = useState("");
    const [deactReason, setDeactReason] = useState("");
    const [reactOpen, setReactOpen] = useState(false);
    const [reactReason, setReactReason] = useState("");

    async function load() {
        setErr(null);
        try {
            const d = await api.adminAffiliateDetail(id);
            setData(d);
            setNewRatePct(Math.round(d.affiliate.commission_rate * 100));
        } catch (e) { setErr(e.message); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function toggleApprove(nextApproved) {
        setBusy(true); setNotice(null);
        try {
            await api.adminUpdateAffiliate(id, { approved: nextApproved, reason: approveReason.trim() });
            setNotice(nextApproved ? "Approved." : "Approval revoked.");
            setApproveOpen(false);
            setApproveReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function saveRate() {
        setBusy(true); setNotice(null);
        try {
            await api.adminUpdateAffiliate(id, {
                commission_rate: Math.max(0, Math.min(1, Number(newRatePct) / 100)),
                reason: rateReason.trim(),
            });
            setNotice("Rate updated.");
            setRateOpen(false);
            setRateReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function deactivate() {
        setBusy(true); setErr(null);
        try {
            await api.adminDeactivateAffiliate(id, deactWord, deactReason.trim());
            setNotice("Affiliate deactivated.");
            setDeactOpen(false); setDeactWord(""); setDeactReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function reactivate() {
        setBusy(true); setErr(null);
        try {
            await api.adminReactivateAffiliate(id, reactReason.trim());
            setNotice("Affiliate reactivated.");
            setReactOpen(false); setReactReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    if (err && !data) return <div className="p-8 text-sm" style={{ color: "#B42318" }}>{err}</div>;
    if (!data) return <div className="p-8 text-sm">Loading…</div>;

    const { affiliate: aff, user: u, stats, referrals, commissions, payouts } = data;

    return (
        <section className="max-w-5xl mx-auto px-6 py-10 space-y-6" data-testid="admin-affiliate-detail-page">
            <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                <Link to="/admin/affiliates" className="hover:underline inline-flex items-center gap-1">
                    <ArrowLeft size={12} /> All affiliates
                </Link>
            </div>

            <div className="st-surface p-6" data-testid="admin-affiliate-header">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>{u.name || "—"}</h1>
                        <div className="mt-1 text-sm" style={{ color: "var(--st-ink-muted)" }}>{u.email}</div>
                        <div className="mt-2 text-xs font-mono" style={{ color: "var(--st-silver-500)" }}>ref: {aff.ref_code}</div>
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setApproveOpen(true)}
                                className={aff.approved ? "btn-ghost text-sm" : "btn-royal text-sm"}
                                data-testid={aff.approved ? "unapprove-btn" : "approve-btn"}>
                            {aff.approved ? "Revoke approval" : "Approve affiliate"}
                        </button>
                        <button type="button" onClick={() => setRateOpen(true)} className="btn-outline-navy text-sm"
                                data-testid="rate-change-btn">
                            Change rate ({Math.round(aff.commission_rate * 100)}%)
                        </button>
                        {!aff.deactivated_at ? (
                            <button type="button" onClick={() => setDeactOpen(true)}
                                    className="text-sm font-semibold rounded-lg px-4 py-2 border"
                                    style={{ background: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA" }}
                                    data-testid="deactivate-open-btn">
                                Deactivate affiliate
                            </button>
                        ) : (
                            <button type="button" onClick={() => setReactOpen(true)} className="btn-royal text-sm"
                                    data-testid="reactivate-open-btn">
                                Reactivate
                            </button>
                        )}
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Approved" value={aff.approved ? "Yes" : "No"} />
                    <Stat label="Rate" value={`${Math.round(aff.commission_rate * 100)}%`} />
                    <Stat label="Payout method" value={aff.payout_method || "— not set"} />
                    <Stat label="Payout handle" value={aff.payout_handle
                        ? <span className="inline-flex items-center gap-2"><span data-testid="admin-aff-payout-handle">{aff.payout_handle}</span>
                            <button type="button" className="link-royal text-xs"
                                    onClick={() => navigator.clipboard.writeText(aff.payout_handle)}
                                    data-testid="copy-payout-handle">Copy</button></span>
                        : <span style={{ color: "#B54708" }}>— not set</span>} />
                    <Stat label="Clicks" value={stats.lifetime_clicks} />
                    <Stat label="Signups" value={stats.lifetime_signups} />
                    <Stat label="Conversions" value={stats.lifetime_conversions} />
                    <Stat label="Earned" value={fmtDollars(stats.lifetime_earned_cents)} />
                </div>
            </div>

            {aff.deactivated_at && (
                <div className="rounded p-3 text-sm" style={{ background: "#F2F4F7", color: "#475467", border: "1px solid var(--st-line)" }} data-testid="affiliate-deactivated-banner">
                    <strong>Deactivated on {new Date(aff.deactivated_at).toLocaleDateString()}.</strong>
                    {aff.deactivated_reason && <> Reason: {aff.deactivated_reason}.</>} New attributions and clicks
                    are blocked. Historical commissions and payouts are preserved.
                </div>
            )}
            {notice && <div className="rounded p-3 text-sm" style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }} data-testid="admin-affiliate-notice">{notice}</div>}
            {err && <div className="rounded p-3 text-sm" style={{ background: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" }}>{err}</div>}

            {/* Commissions */}
            <div className="st-surface p-6" data-testid="admin-affiliate-commissions">
                <div className="flex items-center justify-between">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                        Commissions ({commissions.length})
                    </div>
                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                        Pending: {fmtDollars(stats.pending_payout_cents)} · Paid: {fmtDollars(stats.lifetime_paid_cents)}
                    </div>
                </div>
                {commissions.length === 0 ? (
                    <p className="mt-3 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No commissions.</p>
                ) : (
                    <table className="w-full text-sm mt-4">
                        <thead>
                            <tr style={{ color: "var(--st-ink-muted)" }}>
                                <th className="text-left py-2 tracking-caps text-[10px] font-semibold">Earned</th>
                                <th className="text-right py-2 tracking-caps text-[10px] font-semibold">Gross</th>
                                <th className="text-right py-2 tracking-caps text-[10px] font-semibold">Rate</th>
                                <th className="text-right py-2 tracking-caps text-[10px] font-semibold">Amount</th>
                                <th className="text-center py-2 tracking-caps text-[10px] font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {commissions.slice(0, 25).map((c) => (
                                <tr key={c.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`admin-comm-row-${c.id}`}>
                                    <td className="py-2 text-xs">{new Date(c.earned_at).toLocaleDateString()}</td>
                                    <td className="py-2 text-right">{fmtDollars(c.gross_amount_cents)}</td>
                                    <td className="py-2 text-right">{Math.round(c.commission_rate * 100)}%</td>
                                    <td className="py-2 text-right font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(c.commission_amount_cents)}</td>
                                    <td className="py-2 text-center text-[10px] tracking-caps font-bold">{c.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Referrals */}
            <div className="st-surface p-6" data-testid="admin-affiliate-referrals">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Referred users ({referrals.length})
                </div>
                {referrals.length === 0 ? (
                    <p className="mt-3 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No referrals yet.</p>
                ) : (
                    <ul className="mt-3 space-y-1 text-sm">
                        {referrals.slice(0, 30).map((r) => (
                            <li key={r.id} className="flex items-center justify-between" data-testid={`admin-ref-row-${r.id}`}>
                                <span className="font-mono text-xs" style={{ color: "var(--st-ink)" }}>{r.referred_user_id.slice(0, 12)}…</span>
                                <span className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                                    {new Date(r.attributed_at).toLocaleDateString()} · {r.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Approve dialog */}
            <Dialog open={approveOpen} onOpenChange={(v) => !v && setApproveOpen(false)}>
                <DialogContent data-testid="approve-dialog">
                    <DialogHeader>
                        <DialogTitle>{aff.approved ? "Revoke approval" : "Approve affiliate"}</DialogTitle>
                        <DialogDescription>
                            {aff.approved
                                ? "Revoking prevents any NEW commissions on future conversions. Existing commissions are unaffected."
                                : "Approving lets this affiliate start earning commissions on referred conversions."}
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="st-label">Reason (audit log)</label>
                        <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                  value={approveReason} onChange={(e) => setApproveReason(e.target.value)}
                                  data-testid="approve-reason" />
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setApproveOpen(false)}>Cancel</button>
                        <button type="button" className="btn-royal text-sm"
                                disabled={busy || approveReason.trim().length < 3}
                                onClick={() => toggleApprove(!aff.approved)}
                                data-testid="approve-confirm">
                            {busy ? "Working…" : (aff.approved ? "Revoke" : "Approve")}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rate dialog */}
            <Dialog open={rateOpen} onOpenChange={(v) => !v && setRateOpen(false)}>
                <DialogContent data-testid="rate-dialog">
                    <DialogHeader>
                        <DialogTitle>Commission rate</DialogTitle>
                        <DialogDescription>
                            Rate is snapshotted onto each earned commission at the time of earning. Existing commissions are not retroactively repriced.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="st-label">New rate (%)</label>
                            <input type="number" min="0" max="100" step="1" className="st-input"
                                   value={newRatePct} onChange={(e) => setNewRatePct(e.target.value)}
                                   data-testid="rate-input" />
                        </div>
                        <div>
                            <label className="st-label">Reason (audit log)</label>
                            <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                      value={rateReason} onChange={(e) => setRateReason(e.target.value)}
                                      data-testid="rate-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setRateOpen(false)}>Cancel</button>
                        <button type="button" className="btn-royal text-sm"
                                disabled={busy || rateReason.trim().length < 3}
                                onClick={saveRate}
                                data-testid="rate-confirm">
                            {busy ? "Saving…" : "Save rate"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Deactivate dialog */}
            <Dialog open={deactOpen} onOpenChange={(v) => !v && setDeactOpen(false)}>
                <DialogContent data-testid="deactivate-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ color: "#B42318" }}>Deactivate affiliate</DialogTitle>
                        <DialogDescription>
                            Deactivating stops (1) the referral link from attributing new signups, (2) the dashboard login,
                            but (3) preserves all historical commissions and payouts for accounting. Reversible. Type
                            <strong> DEACTIVATE </strong>to confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="st-label">Reason (required, logged)</label>
                            <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                      value={deactReason} onChange={(e) => setDeactReason(e.target.value)}
                                      data-testid="deactivate-reason" />
                        </div>
                        <div>
                            <label className="st-label">Type DEACTIVATE to confirm</label>
                            <input className="st-input" value={deactWord}
                                   onChange={(e) => setDeactWord(e.target.value)}
                                   placeholder="DEACTIVATE"
                                   data-testid="deactivate-confirm-word" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setDeactOpen(false)}>Cancel</button>
                        <button type="button" className="text-sm font-semibold rounded-lg px-4 py-2"
                                style={{
                                    background: deactWord === "DEACTIVATE" && deactReason.trim().length >= 3 ? "#B42318" : "var(--st-silver-100)",
                                    color: deactWord === "DEACTIVATE" && deactReason.trim().length >= 3 ? "#fff" : "var(--st-silver-500)",
                                }}
                                disabled={busy || deactWord !== "DEACTIVATE" || deactReason.trim().length < 3}
                                onClick={deactivate}
                                data-testid="deactivate-confirm">
                            {busy ? "Deactivating…" : "Deactivate"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reactivate dialog */}
            <Dialog open={reactOpen} onOpenChange={(v) => !v && setReactOpen(false)}>
                <DialogContent data-testid="reactivate-dialog">
                    <DialogHeader>
                        <DialogTitle>Reactivate affiliate</DialogTitle>
                        <DialogDescription>
                            Reactivating restores the referral link and dashboard login. Historical data is unchanged.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="st-label">Reason (audit log)</label>
                        <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                  value={reactReason} onChange={(e) => setReactReason(e.target.value)}
                                  data-testid="reactivate-reason" />
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setReactOpen(false)}>Cancel</button>
                        <button type="button" className="btn-royal text-sm"
                                disabled={busy || reactReason.trim().length < 3}
                                onClick={reactivate}
                                data-testid="reactivate-confirm">
                            {busy ? "Reactivating…" : "Reactivate"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
